import React, { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpArrowDown, faChevronDown, faEllipsisVertical, faMinus } from "@fortawesome/pro-regular-svg-icons";
import { useDispatch } from "react-redux";
import moment from "moment";

import { StatusCode } from "api/protocols";
import {
  GetEmailCampaignAnalytics,
  GetEmailCampaignAnalyticsRecipientsExport,
  EMPTY_EMAIL_CAMPAIGN_ANALYTICS,
  IEmailCampaignAnalytics,
  IEmailCampaignFunnel,
  IEmailCampaignAnalyticsMemberType,
  IEmailCampaignAnalyticsBookingCategory,
  IEmailCampaignTopLink,
  TEmailCampaignAnalyticsDateRange,
  TEmailCampaignAnalyticsMetric,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { showError } from "redux/actions/ui";
import Card from "components/card/Card";
import { ButtonNew } from "components/buttonNew";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Spin from "components/spin/spin";
import DataTable from "components/dataTable/DataTable";
import { Chart } from "react-chartjs-2";
import capitalize from "lodash/capitalize";
import snakeCase from "lodash/snakeCase";
import { useHistory, useParams } from "react-router";
import { downloadCsv } from "helpers/Helpers";
import { getEmailCampaignTopLinkLabel } from "./emailCampaignTopLinks";

interface IProps {
  campaignId: number;
}

interface IPerformanceMetric {
  metric: "Delivered" | "Open" | "Click";
  group: string;
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) {
    return "0%";
  }

  if (Number.isInteger(value)) {
    return `${value}%`;
  }

  return `${value.toFixed(1)}%`;
}

function formatNumber(value: number) {
  if (!Number.isFinite(value)) {
    return "0";
  }

  return value.toLocaleString();
}

function dateRangeToQueryParams(range: TEmailCampaignAnalyticsDateRange): { from?: string; to?: string } {
  if (range === "all_time") {
    return {};
  }

  const days = range === "last_7_days" ? 7 : range === "last_30_days" ? 30 : 90;

  return {
    from: moment().subtract(days, "days").startOf("day").toISOString(),
    to: moment().endOf("day").toISOString(),
  };
}

function AnalyticsEmptyState() {
  return (
    <div className="flex flex-col items-center text-center gap-2 max-w-[360px] mx-auto py-6">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-8 border-blue-50 text-gray-500 text-base mb-2">
        <FontAwesomeIcon icon={faMinus} />
      </div>
      <h4 className="m-0 text-base leading-6 font-semibold text-gray-900">{t("No data available")}</h4>
      <p className="m-0 text-sm leading-5 font-normal text-gray-600">
        {t("Currently, there is no data available.")}
      </p>
    </div>
  );
}

function AnalyticsCard(props: {
  title?: string;
  subtitle?: string;
  detail?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="my-0">
      <Card.Section>
        <div className="flex items-start justify-between gap-4 mb-[20px] border-b border-gray-200 pb-4">
          {(props.title !== undefined || props.subtitle !== undefined) &&
            <div>
              <h3 className="m-0 text-base leading-6 font-semibold text-gray-900">{props.title}</h3>
              <p className="mt-1 mb-0 text-sm leading-5 font-normal text-gray-600">{props.subtitle}</p>
            </div>
          }
          {props.actions ? <div className="flex items-center gap-2 shrink-0">{props.actions}</div> : null}
        </div>
        {props.children}
      </Card.Section>
    </Card>
  );
}

function CardMenu(props: {
  metric: TEmailCampaignAnalyticsMetric;
  sendBatchId?: string | null;
  from?: string;
  to?: string;
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id: campaignId } = useParams<{ id: string }>();

  function viewRecipients() {
    history.push(`/admin/email-marketing/campaigns/${campaignId}/recipients/${props.metric}`);
  }

  function viewPreviewLink() {
    history.push(`/admin/email-marketing/campaigns/${campaignId}/top-links`);
  }

  async function exportList() {
    const resp = await GetEmailCampaignAnalyticsRecipientsExport(
      {
        id: Number(campaignId),
        metric: props.metric,
        send_batch_id: props.sendBatchId ?? undefined,
        from: props.from,
        to: props.to,
      },
      true,
    );

    if (resp.status !== StatusCode.OK || typeof resp.data !== "string") {
      dispatch(showError(resp.message || t("Unable to export campaign recipients.")));
      return;
    }

    downloadCsv(resp.data, `${snakeCase(`email_campaign_${props.metric}`)}_recipients.csv`);
  }

  const menuSections = [
    [{
      label: t("View Recipients"),
      icon: "user",
      onClick: viewRecipients,
    }],
    ...(props.metric === 'top_links' ?
      [
        [{
          label: t("Preview links"),
          icon: "eye",
          onClick: viewPreviewLink,
        }],
      ]
      : []
    ),
    [{
      label: t("Export List"),
      icon: "circle-up",
      onClick: () => void exportList(),
    }],
  ];

  return (
    <div className="-mr-4">
      <NavigationDropdownNew
        showPlainTextLabel
        rightAlign
        hideChevron
        label={
          <ButtonNew className="text-gray-400" type="tertiary" size="ellipsis">
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </ButtonNew>
        }
        sections={menuSections}
      />
    </div>
  );
}

function DateRangeDropdown(props: {
  value: TEmailCampaignAnalyticsDateRange;
  onChange: (value: TEmailCampaignAnalyticsDateRange) => void;
}) {
  const options: Array<{ id: TEmailCampaignAnalyticsDateRange; label: string }> = [
    { id: "last_7_days", label: t("Last 7 days") },
    { id: "last_30_days", label: t("Last 30 days") },
    { id: "last_90_days", label: t("Last 90 days") },
    { id: "all_time", label: t("All time") },
  ];
  const selectedLabel = options.find(option => option.id === props.value)?.label ?? t("Last 7 days");

  return (
    <NavigationDropdownNew
      showPlainTextLabel
      rightAlign
      hideChevron
      label={
        <ButtonNew type="default" size="small" className="text-nowrap">
          {selectedLabel}
          <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
        </ButtonNew>
      }
      sections={[
        options.map(option => ({
          label: option.label,
          onClick: () => props.onChange(option.id),
        })),
      ]}
    />
  );
}

function SortByDropdown(props: {
  onClick: () => void;
}) {

  return (
    <ButtonNew type="default" size="small" onClick={() => props.onClick()}>
      {t("Sort by")}
      <FontAwesomeIcon icon={faArrowUpArrowDown} className="ml-2" />
    </ButtonNew>

  );
}

function EngagementFunnel(props: { funnel: IEmailCampaignFunnel }) {
  const { sent, delivered, opened, clicked } = props.funnel;
  const steps = [
    { label: t("sent"), value: sent, rate: sent > 0 ? 100 : 0 },
    { label: t("delivered"), value: delivered.count, rate: delivered.rate },
    { label: t("opened"), value: opened.count, rate: opened.rate },
    { label: t("clicked"), value: clicked.count, rate: clicked.rate },
  ];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_max-content] gap-x-2 gap-y-[18px] w-full">
      {steps.map((step, index) => {
        const label = (
          <>
            {step.value} {step.label === 'delivered' || step.label === 'opened' ? `(${step.rate}%)` : ""} <span className="text-gray-700 font-normal">{step.label}</span>
          </>
        );

        return (
          <React.Fragment key={step.label}>
            <div className="h-6 flex items-center gap-2">
              <div
                className="h-full rounded shrink-0"
                style={{
                  backgroundColor: memberTypeColor(index),
                  width: `${Math.max(8, Math.min(100, sent > 0 ? (step.value / sent) * 100 : 0))}%`,
                }}
              />
              <div className="text-sm font-semibold whitespace-nowrap">{label}</div>
            </div>
            {/* Reserves the widest label's width so every bar shrinks on the same scale. */}
            <div aria-hidden className="invisible text-sm font-semibold whitespace-nowrap">{label}</div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const TOP_LINKS_CHART_OUTER_RADIUS = 46;
const TOP_LINKS_CHART_INNER_RADIUS = 14;
const TOP_LINKS_CHART_MAX_RING_PITCH = 9;

function TopLinksChart(props: { links: IEmailCampaignTopLink[] }) {
  const maxClicks = Math.max(...props.links.map(link => link.count), 1);
  const pitch = Math.min(
    TOP_LINKS_CHART_MAX_RING_PITCH,
    (TOP_LINKS_CHART_OUTER_RADIUS - TOP_LINKS_CHART_INNER_RADIUS) / props.links.length,
  );
  const strokeWidth = pitch * 0.6;

  return (
    <svg viewBox="0 0 100 100" className="w-full" aria-hidden="true" focusable="false">
      <g transform="rotate(-90 50 50)">
        {props.links.map((link, index) => {
          const radius = TOP_LINKS_CHART_OUTER_RADIUS - index * pitch;
          const circumference = 2 * Math.PI * radius;
          // Keep a sliver of arc visible for low click counts.
          const fraction = Math.max(0.04, link.count / maxClicks);
          const isFullRing = fraction >= 1;

          return (
            <g key={link.url}>
              <circle cx="50" cy="50" r={radius} fill="none" stroke="#F3F4F6" strokeWidth={strokeWidth} />
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke={memberTypeColor(index)}
                strokeWidth={strokeWidth}
                strokeLinecap={isFullRing ? "butt" : "round"}
                strokeDasharray={isFullRing ? undefined : `${fraction * circumference} ${circumference}`}
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function TopLinks(props: { links: IEmailCampaignTopLink[]; campaignId: number }) {
  const history = useHistory();

  return (
    <div className="flex items-center gap-6">
      <div className="w-[180px] shrink-0">
        <TopLinksChart links={props.links} />
      </div>
      <ul className="flex-1 min-w-0 flex flex-col gap-3 m-0 p-0 list-none">
        {props.links.map((link, index) => (
          <li key={link.url}>
            <button
              type="button"
              className="flex w-full items-center gap-2 min-w-0 text-sm leading-5 font-medium text-gray-900 hover:text-primary-700 text-left"
              onClick={() =>
                history.push(`/admin/email-marketing/campaigns/${props.campaignId}/top-links`, {
                  selectedUrl: link.url,
                })
              }
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: memberTypeColor(index) }} />
              <span className="min-w-0 truncate" title={link.url}>
                {getEmailCampaignTopLinkLabel(link)}
              </span>
              <span className="shrink-0">({formatNumber(link.count)})</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MemberTypePerformance(props: {
  rows: IEmailCampaignAnalyticsBookingCategory[];
  setPerformanceMetric: (metric: IPerformanceMetric) => void;
  performanceMetric: IPerformanceMetric | null;
}) {
  return (
    <DataTable
      className="h-full"
      textWrap={true}
      columns={[
        { label: t("Category") },
        { label: t("Delivered") },
        { label: t("Open Rate") },
        { label: t("Click Rate") },
      ]}
    >
      {props.rows.map(row => {
        const isSelectedGroup = props.performanceMetric?.group === row.booking_category;

        return (
          <tr key={row.booking_category}>
            <td className="font-bold">{t(capitalize(row.booking_category))}</td>
            <td
              className={
                "hover:cursor-pointer hover:bg-gray-50" +
                (isSelectedGroup && props.performanceMetric?.metric === "Delivered" ? " bg-gray-50" : "")
              }
              onClick={() => props.setPerformanceMetric({ group: row.booking_category, metric: "Delivered" })}
            >
              {formatNumber(row.delivered.count)}
            </td>
            <td
              className={
                "hover:cursor-pointer hover:bg-gray-50" +
                (isSelectedGroup && props.performanceMetric?.metric === "Open" ? " bg-gray-50" : "")
              }
              onClick={() => props.setPerformanceMetric({ group: row.booking_category, metric: "Open" })}
            >
              {formatPercent(row.opened.rate)}
            </td>
            <td
              className={
                "hover:cursor-pointer hover:bg-gray-50" +
                (isSelectedGroup && props.performanceMetric?.metric === "Click" ? " bg-gray-50" : "")
              }
              onClick={() => props.setPerformanceMetric({ group: row.booking_category, metric: "Click" })}
            >
              {formatPercent(row.clicked.rate)}
            </td>
          </tr>
        );
      })}
    </DataTable>
  );
}

function PerformanceMetricEmptyState() {
  return (
    <Card className="my-0">
      <div className="flex flex-col justify-center items-center text-center gap-2 h-full p-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 border-8 border-primary-50 mb-2">
          <img src="/public/images/pie-circle-03.svg" width="20" height="20" alt="" aria-hidden="true" />
        </div>
        <h4 className="m-0 text-base leading-6 font-semibold text-gray-900">{t("Click a metric to see details")}</h4>
        <p className="m-0 text-sm leading-5 font-normal text-gray-600">
          {t("Click open or click rate cell in the table to see customer type details.")}
        </p>
      </div>
    </Card>
  );
}

const MEMBER_TYPE_CHART_COLORS = [
  "#0072B2",
  "#D55E00",
  "#E69F00",
  "#CC79A7",
  "#4B9E86",
  "#7C3AED",
  "#F4D03F",
  "#4CC9E8",
  "#6D3B9E",
  "#D92D20",
];

function memberTypeColor(index: number) {
  return MEMBER_TYPE_CHART_COLORS[index % MEMBER_TYPE_CHART_COLORS.length];
}

function PerformanceMetricDetails(props: {
  memberTypes: IEmailCampaignAnalyticsMemberType[];
  metric: IPerformanceMetric["metric"];
}) {
  const getMetric = (memberType: IEmailCampaignAnalyticsMemberType) => {
    switch (props.metric) {
      case "Delivered":
        return memberType.delivered;
      case "Click":
        return memberType.clicked;
      default:
        return memberType.opened;
    }
  };

  return (
    <div className="flex items-center gap-6">
      <div className="w-[150px]">
        <Chart
          type="doughnut"
          data={{
            datasets: [
              {
                data: props.memberTypes.map(memberType => getMetric(memberType).count),
                backgroundColor: props.memberTypes.map((_, index) => memberTypeColor(index)),
                borderWidth: 0,
              },
            ],
            labels: props.memberTypes.map(memberType => memberType.title),
          }}
          options={{
            cutout: "60%",
            plugins: { legend: { display: false } },
          }}
          width={150}
        />
      </div>
      <div className="grid grid-cols-1 2xl:grid-cols-2 w-full">
        {props.memberTypes.map((memberType, index) => (
          <div className="flex items-center justify-start gap-2" key={memberType.title}>
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: memberTypeColor(index) }}
            />

            <span className="text-sm leading-5 font-medium text-gray-900">
              {memberType.title} <span className="text-gray-600">: {formatPercent(getMetric(memberType).rate)}</span>
            </span>
          </div>
        ))}
      </div>
    </div >
  );
}

export default function EmailCampaignAnalytics(props: IProps) {
  const { campaignId } = props;
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<TEmailCampaignAnalyticsDateRange>("last_7_days");
  const [topLinksSortAscending, setTopLinksSortAscending] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<IEmailCampaignAnalytics>(EMPTY_EMAIL_CAMPAIGN_ANALYTICS);
  const [performanceMetric, setPerformanceMetric] = useState<IPerformanceMetric | null>(null);

  useEffect(() => {
    if (!campaignId) {
      return;
    }

    let cancelled = false;

    async function loadAnalytics() {
      setLoading(true);
      // setPerformanceMetric(null);
      const resp = await GetEmailCampaignAnalytics(
        {
          id: campaignId,
          ...dateRangeToQueryParams(dateRange),
        },
        false,
      );

      if (cancelled) {
        return;
      }

      if (resp.status !== StatusCode.OK || !resp.data) {
        dispatch(showError(resp.message || t("Unable to load campaign analytics.")));
        setAnalytics(EMPTY_EMAIL_CAMPAIGN_ANALYTICS);
        setLoading(false);
        return;
      }

      setAnalytics({
        ...EMPTY_EMAIL_CAMPAIGN_ANALYTICS,
        ...resp.data,
        summary: {
          ...EMPTY_EMAIL_CAMPAIGN_ANALYTICS.summary,
          ...resp.data.summary,
        },
        funnel: {
          ...EMPTY_EMAIL_CAMPAIGN_ANALYTICS.funnel,
          ...resp.data.funnel,
        },
        top_links: resp.data.top_links ?? [],
        booking_categories: resp.data.booking_categories ?? [],
        member_types: resp.data.member_types ?? [],
      });
      setLoading(false);
    }

    void loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [campaignId, dateRange, dispatch, t]);

  const sortedTopLinks = useMemo(() => {
    const links = [...(analytics.top_links ?? [])];
    if (topLinksSortAscending === true) {
      return links.sort((a, b) => a.count - b.count);
    }
    return links.sort((a, b) => b.count - a.count);
  }, [analytics.top_links, topLinksSortAscending]);

  const hasFunnelData = (analytics.funnel?.sent ?? 0) > 0;
  const hasTopLinks = sortedTopLinks.length > 0;
  const hasMemberTypes = (analytics.member_types ?? []).length > 0;

  const selectedMemberTypes = useMemo(() => {
    if (!performanceMetric) {
      return [];
    }

    return (analytics.member_types ?? []).filter(memberType => memberType.booking_category === performanceMetric.group);
  }, [analytics.member_types, performanceMetric]);

  const hasSelectedMemberTypes = selectedMemberTypes.length > 0;
  const exportFilters = {
    sendBatchId: analytics.send_batch_id,
    ...dateRangeToQueryParams(dateRange),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <Spin className="size-64!" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AnalyticsCard
          title={t("Total deliveries")}
          subtitle={t("Total number of emails delivered.")}
          actions={<CardMenu metric="delivered" {...exportFilters} />}
        >
          <div className="text-2xl font-semibold text-gray-900">
            {formatNumber(analytics.summary.delivered.count)}
          </div>
          <div className="text-sm text-gray-500 mt-3">
            {formatPercent(analytics.summary.delivered.rate)} {t("delivered")}
          </div>
        </AnalyticsCard>
        <AnalyticsCard
          title={t("Open rate")}
          subtitle={t("% of recipients who opened.")}
          actions={<CardMenu metric="opened" {...exportFilters} />}
        >
          <div className="text-2xl font-semibold text-gray-900">
            {formatPercent(analytics.summary.opened.rate)}
          </div>
          <div className="text-sm text-gray-500 mt-3">
            {formatNumber(analytics.summary.opened.count)} {t("opened")}
          </div>
        </AnalyticsCard>
        <AnalyticsCard
          title={t("Bounce rate")}
          subtitle={t("% of failed deliveries.")}
          actions={<CardMenu metric="bounced" {...exportFilters} />}
        >
          <div className="text-2xl font-semibold text-gray-900 mt-3">
            {formatPercent(analytics.summary.bounced.rate)}
          </div>
          <div className="text-sm text-gray-500 mt-3">
            {formatNumber(analytics.summary.bounced.count)} {t("bounced")}
          </div>
        </AnalyticsCard>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <AnalyticsCard
          title={t("Engagement funnel")}
          subtitle={t("Sent → Delivered → Open → Click")}
          actions={
            <>
              <DateRangeDropdown value={dateRange} onChange={setDateRange} />
              <CardMenu metric="funnel" {...exportFilters} />
            </>
          }
        >
          <div
            className={`flex flex-col ${hasFunnelData ? "justify-start min-h-0" : "justify-center"}`}
          >
            {hasFunnelData ? (
              <EngagementFunnel funnel={analytics.funnel} />
            ) : (
              <AnalyticsEmptyState />
            )}
          </div>
        </AnalyticsCard>

        <AnalyticsCard
          title={t("Top links")}
          subtitle={t("Most-clicked links.")}
          actions={<>
            <SortByDropdown
              value={topLinksSortAscending}
              onClick={() => setTopLinksSortAscending(!topLinksSortAscending)}
            />
            <CardMenu metric="top_links" {...exportFilters} />
          </>}
        >
          <div
            className={`flex flex-col ${hasTopLinks ? "justify-start min-h-0" : "justify-center"}`}
          >
            {hasTopLinks ? (
              <TopLinks links={sortedTopLinks} campaignId={props.campaignId} />
            ) : (
              <AnalyticsEmptyState />
            )}
          </div>
        </AnalyticsCard>
      </div>

      <AnalyticsCard
        title={t("Customer performance")}
        subtitle={t("Compare members and public performance by delivered count, open and click rate.")}
        actions={<>
          <DateRangeDropdown value={dateRange} onChange={setDateRange} />
          <CardMenu metric="member_types" {...exportFilters} />
        </>}
      >
        {hasMemberTypes ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MemberTypePerformance
              rows={analytics.booking_categories}
              performanceMetric={performanceMetric}
              setPerformanceMetric={setPerformanceMetric}
            />
            {performanceMetric === null
              ? <PerformanceMetricEmptyState />
              : <AnalyticsCard
                title={t("{{metric}} performance ({{memberType}})", {
                  metric: performanceMetric.metric,
                  memberType: t(performanceMetric.group),
                })}
                subtitle={t("Compare open rates by customer type.")}
              >
                <div
                  className={`flex flex-col ${hasSelectedMemberTypes ? "justify-start min-h-0" : "justify-center"}`}
                >
                  {hasSelectedMemberTypes ? (
                    <PerformanceMetricDetails
                      memberTypes={selectedMemberTypes}
                      metric={performanceMetric.metric}
                    />
                  ) : (
                    <AnalyticsEmptyState />
                  )}
                </div>
              </AnalyticsCard>
            }
          </div>
        ) : (
          <AnalyticsEmptyState />
        )}
      </AnalyticsCard>
    </div >
  );
}
